import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2, Check, Copy, ExternalLink, Eye, Mail, MessageCircle, Pencil, RefreshCw, Search, Send, ShieldCheck, Sparkles, Trash2, UserPlus, Users,
} from 'lucide-react';
import {
  ADRESS_HINWEIS, ANMELDE_DOMAIN, SICHT_INFO, anredeName, feedbackLink, inZwischenablage, istRandoriAdresse, listePersonen, mailtoLink, neuerLink,
  personEinladen, personLoeschen, personSpeichern, personenVorschlaege, teilenText, wannText, whatsappLink,
  type Ansprechperson, type PersonSicht, type PersonVorschlag, type PlanungsMail,
} from '../../lib/planung';
import { SPORTSCHULEN } from '../../lib/sportschulen';
import { Modal } from '../../components/Modal/Modal';
import { useToast } from '../../components/Toast';
import { Avatar } from '../../components/Planung/Avatar';
import { useAuthStore } from '../../stores/authStore';

const SICHTEN: PersonSicht[] = ['zugeordnet', 'sportschulen', 'alle'];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const EINLADUNG_BETREFF = 'Mails zum Abstimmen – Randori-Pro';

/** Studioaccounts legt der stündliche Abgleich aus dem CRM an (Migration 0051). */
function StudioBadge() {
  return (
    <span className="badge badge-info" title="Kommt automatisch aus dem CRM-Studioaccount: Name, Adresse und Sportschule pflegt das CRM, der Abgleich läuft stündlich.">
      <Building2 size={12} aria-hidden="true" /> Studioaccount · automatisch
    </span>
  );
}

/**
 * Link kopieren, per WhatsApp oder Mail teilen, per Mail einladen. Admins
 * öffnen den Link auch selbst (oeffnen): Die Seite zeigt dann, was die Person
 * sieht — nur zum Ansehen (Admin-Ansicht, Migration 0052).
 */
function TeilenKnoepfe({ person, gross = false, oeffnen = false }: { person: Ansprechperson; gross?: boolean; oeffnen?: boolean }) {
  const toast = useToast();
  const [kopiert, setKopiert] = useState(false);
  const link = feedbackLink(person.token);
  const text = teilenText(person.name, null, link);
  const einladen = useMutation({
    mutationFn: () => personEinladen(person.id),
    onSuccess: () => toast.success(`Einladung an ${person.name} verschickt.`),
    onError: (e) => toast.error(`Die Einladung ging nicht raus: ${(e as Error).message}`),
  });

  const kopieren = async () => {
    if (await inZwischenablage(link)) {
      setKopiert(true);
      toast.success(`Link für ${person.name} kopiert.`);
      window.setTimeout(() => setKopiert(false), 2000);
    } else {
      toast.error('Kopieren ging nicht — markier den Link und kopier ihn von Hand.');
    }
  };

  if (!person.aktiv) {
    return <span className="hint gm-teilen">Zum Teilen den Link unter „Bearbeiten“ wieder einschalten.</span>;
  }

  return (
    <div className={`gm-teilen${gross ? ' gm-teilen--gross' : ''}`}>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => void kopieren()}>
        {kopiert ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />} {kopiert ? 'Kopiert' : 'Link kopieren'}
      </button>
      {oeffnen && (
        <a
          className="action-btn"
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Übersicht so öffnen, wie ${person.name} sie sieht (Admin-Ansicht, neuer Tab)`}
          title="Öffnen — so sieht die Person ihre Mails (nur ansehen)"
        >
          <ExternalLink size={15} aria-hidden="true" />
        </a>
      )}
      <a
        className="action-btn"
        href={whatsappLink(text)}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Link per WhatsApp an ${person.name} schicken`}
        title="Per WhatsApp teilen"
      >
        <MessageCircle size={15} aria-hidden="true" />
      </a>
      <a
        className="action-btn"
        href={mailtoLink(person.email, EINLADUNG_BETREFF, text)}
        aria-label={`Link per eigener Mail an ${person.name} schicken`}
        title="In Deinem Mailprogramm öffnen"
      >
        <Mail size={15} aria-hidden="true" />
      </a>
      {person.email && (
        <button
          type="button"
          className="action-btn"
          onClick={() => einladen.mutate()}
          disabled={einladen.isPending}
          aria-label={`Einladung mit dem Link an ${person.name} mailen`}
          title="Einladung vom Mailtool verschicken (mit Liste der angefragten Mails)"
        >
          {einladen.isPending ? <span className="loading-spinner gm-mini-spinner" /> : <Send size={15} aria-hidden="true" />}
        </button>
      )}
    </div>
  );
}

type FormularProps = {
  person: Ansprechperson | null;
  onClose: () => void;
  onGespeichert: () => void;
};

/** Anlegen und Bearbeiten. Nach dem Anlegen zeigt der Dialog gleich den Link zum Teilen. */
function PersonFormular({ person, onClose, onGespeichert }: FormularProps) {
  const toast = useToast();
  const admin = useAuthStore((s) => !!s.user && !s.user.ohneRolle && s.user.role === 'admin');
  const [name, setName] = useState(person?.name ?? '');
  const [email, setEmail] = useState(person?.email ?? '');
  const [rolle, setRolle] = useState(person?.rolle_text ?? '');
  const [sicht, setSicht] = useState<PersonSicht>(person?.sicht ?? 'zugeordnet');
  const [schulen, setSchulen] = useState<string[]>(person?.sportschulen ?? []);
  const [aktiv, setAktiv] = useState(person?.aktiv ?? true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [angelegt, setAngelegt] = useState<Ansprechperson | null>(null);
  // Name, Adresse, Sicht und Sportschulen kommen beim Studioaccount aus dem CRM.
  const studio = person?.herkunft === 'studioaccount';

  const speichern = useMutation({
    mutationFn: () => personSpeichern({
      id: person?.id, herkunft: person?.herkunft, name, email: email || null, rolle_text: rolle || null, sicht,
      sportschulen: sicht === 'sportschulen' ? schulen : [], aktiv,
    }),
    onSuccess: (p) => {
      onGespeichert();
      if (person) { toast.success(`${p.name} gespeichert.`); onClose(); } else setAngelegt(p);
    },
    onError: (e) => setFehler((e as Error).message),
  });

  const absenden = (e: FormEvent) => {
    e.preventDefault();
    setFehler(null);
    if (!studio) {
      if (!name.trim()) return setFehler('Bitte gib einen Namen ein.');
      if (!email.trim()) return setFehler(`Bitte gib die Adresse @${ANMELDE_DOMAIN} der Person ein — ohne sie kann sie den Link nicht öffnen.`);
      if (!EMAIL_RE.test(email.trim())) return setFehler('Die E-Mail-Adresse sieht nicht vollständig aus.');
      if (!istRandoriAdresse(email)) return setFehler(`Nur Adressen @${ANMELDE_DOMAIN}: Feedback-Links gibt es nur für Randori-Pro-Konten.`);
      if (sicht === 'sportschulen' && schulen.length === 0) return setFehler('Wähl mindestens eine Sportschule — oder eine andere Sicht.');
    }
    speichern.mutate();
  };

  const schuleUmschalten = (s: string) => setSchulen((v) => (v.includes(s) ? v.filter((x) => x !== s) : [...v, s]));

  if (angelegt) {
    return (
      <div className="gm-form">
        <div className="gm-fertig">
          <Avatar name={angelegt.name} groesse="md" />
          <div>
            <strong>{angelegt.name} ist angelegt.</strong>
            <p className="hint m-0">Schick ihr oder ihm jetzt den persönlichen Link. Damit sieht {anredeName(angelegt.name)} {sicht === 'zugeordnet' ? 'die Mails, zu denen Du um Feedback bittest' : sicht === 'alle' ? 'alle geplanten Mails' : `die Mails von ${schulen.join(', ')}`}. Zum Ansehen und Kommentieren meldet sie oder er sich mit dem Google-Konto dieser Adresse an.</p>
          </div>
        </div>
        <input className="input gm-link-feld" readOnly value={feedbackLink(angelegt.token)} aria-label="Persönlicher Link" onFocus={(e) => e.currentTarget.select()} />
        <TeilenKnoepfe person={angelegt} gross oeffnen={admin} />
        <div className="gm-form-fuss">
          <button type="button" className="btn btn-primary" onClick={onClose}>Fertig</button>
        </div>
      </div>
    );
  }

  return (
    <form className="gm-form" onSubmit={absenden} noValidate>
      {studio && (
        <p className="hint m-0">
          <StudioBadge />{' '}
          Name, Adresse und Sportschule kommen aus dem CRM-Studioaccount und lassen sich hier nicht ändern. Der Studioaccount sieht alle
          Mails seiner Sportschule. Du kannst die Rolle beschriften und den Link ab- oder einschalten.
        </p>
      )}
      <div className="grid-2 gm-form-raster">
        <div className="field">
          <label htmlFor="ap-name">Name</label>
          <input id="ap-name" className="input" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} autoComplete="off" required autoFocus={!studio} readOnly={studio} aria-readonly={studio || undefined} />
        </div>
        <div className="field">
          <label htmlFor="ap-rolle">Rolle oder Funktion</label>
          <input id="ap-rolle" className="input" value={rolle} maxLength={120} placeholder="z. B. Studioleitung Spandau, Trainer" onChange={(e) => setRolle(e.target.value)} autoComplete="off" />
        </div>
      </div>
      <div className="field">
        <label htmlFor="ap-email">E-Mail</label>
        <input
          id="ap-email" type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off"
          required readOnly={studio} aria-readonly={studio || undefined} placeholder={`vorname.nachname@${ANMELDE_DOMAIN}`}
          aria-invalid={!studio && EMAIL_RE.test(email.trim()) && !istRandoriAdresse(email) ? true : undefined}
        />
        <span className="hint">{ADRESS_HINWEIS} Die Einladung aus dem Mailtool geht auch an diese Adresse.</span>
        {!studio && EMAIL_RE.test(email.trim()) && !istRandoriAdresse(email) && (
          <span className="hint gm-hinweis-extern" role="note">Nur Adressen @{ANMELDE_DOMAIN} — mit dieser Adresse lässt sich die Person nicht anlegen.</span>
        )}
      </div>

      <fieldset className="gm-sicht" disabled={studio}>
        <legend>Was sieht die Person über ihren Link?</legend>
        {SICHTEN.map((s) => (
          <label key={s} className={`gm-sicht-option${sicht === s ? ' aktiv' : ''}`}>
            <input type="radio" name="ap-sicht" value={s} checked={sicht === s} onChange={() => setSicht(s)} />
            <span>
              <strong>{SICHT_INFO[s].label}</strong>
              <span className="hint">{SICHT_INFO[s].text}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {sicht === 'sportschulen' && (
        <fieldset className="gm-schulwahl" disabled={studio}>
          <legend>Sportschulen</legend>
          <div className="chip-row">
            {SPORTSCHULEN.map((s) => (
              <button key={s.name} type="button" className={`chip${schulen.includes(s.name) ? ' active' : ''}`} aria-pressed={schulen.includes(s.name)} onClick={() => schuleUmschalten(s.name)}>
                {s.name}
              </button>
            ))}
          </div>
        </fieldset>
      )}

      {person && (
        <label className="checkbox-row gm-aktiv">
          <input type="checkbox" checked={aktiv} onChange={(e) => setAktiv(e.target.checked)} />
          <span>Link ist aktiv <span className="hint">— aus: Der Link funktioniert nicht, bis Du ihn wieder einschaltest.</span></span>
        </label>
      )}

      {fehler && <div className="alert alert-danger m-0" role="alert">{fehler}</div>}

      <div className="gm-form-fuss">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
        <button type="submit" className="btn btn-primary" disabled={speichern.isPending}>
          {speichern.isPending ? 'Speichert …' : person ? 'Speichern' : 'Person anlegen'}
        </button>
      </div>
    </form>
  );
}

/** Reiter „Ansprechpersonen": wer über einen persönlichen Link mitliest und was sie oder er sieht. */
export function PersonenReiter({ mails }: { mails: PlanungsMail[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  // Admins öffnen jeden Link selbst (Admin-Ansicht, nur lesen).
  const admin = useAuthStore((s) => !!s.user && !s.user.ohneRolle && s.user.role === 'admin');
  const [suche, setSuche] = useState('');
  // null = zu, { person: null } = neue Person anlegen
  const [dialog, setDialog] = useState<{ person: Ansprechperson | null } | null>(null);
  const [alleVorschlaege, setAlleVorschlaege] = useState(false);

  const personen = useQuery({ queryKey: ['ansprechpersonen'], queryFn: listePersonen });
  const vorschlaege = useQuery({ queryKey: ['ansprechpersonen-vorschlaege'], queryFn: () => personenVorschlaege(), staleTime: 5 * 60_000 });

  const neuLaden = () => {
    void qc.invalidateQueries({ queryKey: ['ansprechpersonen'] });
    void qc.invalidateQueries({ queryKey: ['ansprechpersonen-vorschlaege'] });
    void qc.invalidateQueries({ queryKey: ['planung-liste'] });
    void qc.invalidateQueries({ queryKey: ['planung-mail'] });
  };

  const uebernehmen = useMutation({
    mutationFn: (v: PersonVorschlag) => personSpeichern({
      name: v.name, email: v.email, rolle_text: v.rolle_text,
      sicht: v.sportschulen.length ? 'sportschulen' : 'zugeordnet', sportschulen: v.sportschulen,
    }),
    onSuccess: (p) => { neuLaden(); toast.success(`${p.name} ist jetzt Ansprechperson.`); },
    onError: (e) => toast.error(`Übernehmen ging nicht: ${(e as Error).message}`),
  });

  const linkNeu = useMutation({
    mutationFn: (p: Ansprechperson) => neuerLink(p.id),
    onSuccess: async (token, p) => {
      neuLaden();
      // Gesperrter Link: neu erstellt, aber nicht zum Teilen in die Zwischenablage.
      if (!p.aktiv) return toast.success(`Neuer Link für ${p.name} erstellt — er funktioniert erst, wenn Du ihn wieder einschaltest.`);
      const kopiert = await inZwischenablage(feedbackLink(token));
      toast.success(kopiert ? `Neuer Link für ${p.name} — er ist in der Zwischenablage.` : `Neuer Link für ${p.name} erstellt.`);
    },
    onError: (e) => toast.error(`Neuer Link ging nicht: ${(e as Error).message}`),
  });

  const loeschen = useMutation({
    mutationFn: (p: Ansprechperson) => personLoeschen(p.id),
    onSuccess: (_d, p) => { neuLaden(); toast.success(`${p.name} gelöscht.`); },
    onError: (e) => toast.error(`Löschen ging nicht: ${(e as Error).message}`),
  });

  // Mails je Person, zu denen sie um Feedback gebeten ist (aus der Planungsliste).
  // Was sie zusätzlich über Sportschule oder „alle Mails“ sieht, zeigt jede Mail
  // selbst („Sieht die Mail auch“) — hier steht es nur als Zusatz.
  const zuordnung = useMemo(() => {
    const karte = new Map<string, { mails: PlanungsMail[]; offen: number }>();
    for (const m of mails) {
      for (const a of m.anfragen) {
        if (!karte.has(a.person_id)) karte.set(a.person_id, { mails: [], offen: 0 });
        const z = karte.get(a.person_id)!;
        z.mails.push(m);
        if (a.antwort === 'offen' && m.status !== 'sent') z.offen += 1;
      }
    }
    return karte;
  }, [mails]);

  const liste = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return (personen.data ?? [])
      .filter((p) => !q || [p.name, p.rolle_text, p.email, ...p.sportschulen].some((x) => x?.toLowerCase().includes(q)))
      .sort((a, b) => Number(b.aktiv) - Number(a.aktiv) || a.name.localeCompare(b.name, 'de'));
  }, [personen.data, suche]);

  // Nur Vorschläge, die sich auch anlegen lassen (Adresse @randori-pro.de).
  const vorschlagListe = useMemo(() => (vorschlaege.data ?? []).filter((v) => istRandoriAdresse(v.email)), [vorschlaege.data]);
  const sichtbareVorschlaege = alleVorschlaege ? vorschlagListe : vorschlagListe.slice(0, 8);

  return (
    <div className="gm-reiter-inhalt">
      <div className="gm-werkzeug">
        <div className="search-input-wrapper">
          <Search size={16} aria-hidden="true" />
          <input type="search" className="search-input" placeholder="Name, Rolle oder Sportschule …" value={suche} onChange={(e) => setSuche(e.target.value)} aria-label="Ansprechpersonen durchsuchen" />
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setDialog({ person: null })}>
          <UserPlus size={16} aria-hidden="true" /> Person anlegen
        </button>
      </div>

      <p className="hint gm-personen-hinweis">
        <ShieldCheck size={14} aria-hidden="true" />
        <span>
          Links gibt es nur für Adressen @{ANMELDE_DOMAIN}. Ansehen, kommentieren, markieren und ändern geht erst, wenn die Person mit dem
          Google-Konto ihrer hier hinterlegten Adresse angemeldet ist — so liest und schreibt niemand unter fremdem Namen. Die Studioaccounts
          der Sportschulen stehen automatisch hier und sehen alle Mails ihrer Sportschule. Hier steht je Person der Link zu ihrer Übersicht;
          den Link zu einer einzelnen Mail findest Du in der Mail selbst im Kasten „Ansprechpersonen“.
          {admin && ' Als Admin öffnest Du jeden Link mit dem Knopf neben „Link kopieren“ und siehst, was die Person sieht — nur zum Ansehen.'}
        </span>
      </p>

      {vorschlagListe.length > 0 && (
        <div className="gm-vorschlaege">
          <p className="gm-vorschlaege-titel"><Sparkles size={14} aria-hidden="true" /> Aus dem CRM — mit einem Klick übernehmen</p>
          <div className="chip-row">
            {sichtbareVorschlaege.map((v) => (
              <button
                key={`${v.name}-${v.email ?? ''}`}
                type="button"
                className="chip gm-vorschlag"
                disabled={uebernehmen.isPending}
                onClick={() => uebernehmen.mutate(v)}
                title={v.sportschulen.length ? `Übernehmen — sieht danach die Mails von ${v.sportschulen.join(', ')}` : 'Übernehmen — sieht danach nur zugeordnete Mails'}
              >
                <UserPlus size={13} aria-hidden="true" />
                <span>{v.name}</span>
                {v.rolle_text && <span className="gm-vorschlag-rolle">{v.rolle_text}</span>}
              </button>
            ))}
            {vorschlagListe.length > 8 && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAlleVorschlaege((v) => !v)}>
                {alleVorschlaege ? 'Weniger zeigen' : `Alle ${vorschlagListe.length} zeigen`}
              </button>
            )}
          </div>
        </div>
      )}

      {personen.isLoading ? (
        <div className="gm-laden"><div className="loading-spinner" /></div>
      ) : personen.isError && !personen.data ? (
        <div className="alert alert-danger" role="alert">
          <span>Die Ansprechpersonen ließen sich nicht laden: {(personen.error as Error).message}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void personen.refetch()}><RefreshCw size={14} aria-hidden="true" /> Nochmal</button>
        </div>
      ) : liste.length === 0 ? (
        <div className="gm-leer">
          <Users size={32} aria-hidden="true" />
          <p>
            {suche ? 'Niemand passt zur Suche.'
              : `Noch keine Ansprechpersonen. Leg Trainer oder Studioleitungen mit ihrer Adresse @${ANMELDE_DOMAIN} an — sie bekommen einen persönlichen Link und öffnen ihn mit dem Google-Konto dieser Adresse.`}
          </p>
        </div>
      ) : (
        <ul className="gm-personen">
          {liste.map((p) => {
            const z = zuordnung.get(p.id);
            const anzahl = z?.mails.length ?? 0;
            return (
              <li key={p.id} className={`gm-person${p.aktiv ? '' : ' gm-person--aus'}`}>
                <div className="gm-person-kopf">
                  <Avatar name={p.name} groesse="md" />
                  <div className="gm-person-name">
                    <strong>{p.name}</strong>
                    {p.rolle_text && <span>{p.rolle_text}</span>}
                    {p.email
                      ? <span className="gm-person-mail">{p.email}</span>
                      : <span className="gm-person-ohne-mail">Ohne Adresse kann die Person den Link nicht öffnen</span>}
                    {p.email && !istRandoriAdresse(p.email) && (
                      <span className="gm-person-ohne-mail">Keine Adresse @{ANMELDE_DOMAIN} — der Link funktioniert nicht</span>
                    )}
                    {p.herkunft === 'studioaccount' && <StudioBadge />}
                  </div>
                  {!p.aktiv && <span className="badge badge-muted">Link gesperrt</span>}
                </div>

                <div className="gm-person-sicht" title={SICHT_INFO[p.sicht].text}>
                  <Eye size={14} aria-hidden="true" />
                  <span>{SICHT_INFO[p.sicht].label}</span>
                </div>
                {p.sicht === 'sportschulen' && p.sportschulen.length > 0 && (
                  <div className="pl-schulen">{p.sportschulen.map((s) => <span key={s} className="pl-schule">{s}</span>)}</div>
                )}

                <dl className="gm-person-zahlen">
                  <div title={z?.mails.map((m) => m.betreff || m.name).join('\n') || undefined}>
                    <dt>Angefragt</dt>
                    <dd>
                      {anzahl} {anzahl === 1 ? 'Mail' : 'Mails'}{z?.offen ? <span className="gm-person-offen"> · {z.offen} offen</span> : null}
                      {p.sicht === 'alle' ? (
                        <span className="gm-person-dazu">sieht dazu alle geplanten Mails</span>
                      ) : p.sicht === 'sportschulen' && p.sportschulen.length > 0 ? (
                        <span className="gm-person-dazu">sieht dazu alle Mails von {p.sportschulen.join(', ')}</span>
                      ) : null}
                    </dd>
                  </div>
                  <div>
                    <dt>Zuletzt da</dt>
                    <dd>{p.zuletzt_gesehen ? wannText(p.zuletzt_gesehen) : 'Link noch nicht geöffnet'}</dd>
                  </div>
                </dl>

                <div className="gm-person-fuss">
                  <TeilenKnoepfe person={p} oeffnen={admin} />
                  <div className="action-btns">
                    <button type="button" className="action-btn" onClick={() => setDialog({ person: p })} aria-label={`${p.name} bearbeiten`} title="Bearbeiten">
                      <Pencil size={15} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="action-btn"
                      disabled={linkNeu.isPending}
                      onClick={() => {
                        if (confirm(`Neuen Link für ${p.name} erstellen? Der bisherige Link funktioniert danach nicht mehr — schick ${anredeName(p.name)} den neuen.`)) linkNeu.mutate(p);
                      }}
                      aria-label={`Neuen Link für ${p.name} erstellen`}
                      title="Neuer Link (der alte hört auf zu funktionieren)"
                    >
                      <RefreshCw size={15} aria-hidden="true" />
                    </button>
                    {p.herkunft !== 'studioaccount' && (
                      <button
                        type="button"
                        className="action-btn danger"
                        disabled={loeschen.isPending}
                        onClick={() => {
                          if (confirm(`${p.name} löschen? Der Link funktioniert dann nicht mehr und die Zuordnungen zu Mails entfallen. Bisherige Kommentare bleiben stehen.`)) loeschen.mutate(p);
                        }}
                        aria-label={`${p.name} löschen`}
                        title="Löschen"
                      >
                        <Trash2 size={15} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Modal isOpen={!!dialog} onClose={() => setDialog(null)} title={dialog?.person ? `${dialog.person.name} bearbeiten` : 'Ansprechperson anlegen'} size="md">
        {dialog && (
          <PersonFormular
            key={dialog.person?.id ?? 'neu'}
            person={dialog.person}
            onClose={() => setDialog(null)}
            onGespeichert={neuLaden}
          />
        )}
      </Modal>
    </div>
  );
}
